import Link from "next/link";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import type { ParagraphDarkHeroBack as ParagraphDarkHeroBackData } from "@/lib/api/cms-catalog";
import type { ParagraphTranslations } from "../Paragraph.i18n";
import { HtmlText } from "../shared/HtmlText";
import styles from "./ParagraphDarkHeroBack.module.css";

/**
 * Layout 90 - `PARAGRAPH_DARK_HERO_BACK_LAYOUT`: dark hero z back-linkiem + kategoria.
 *
 * Dark full-bleed section per-paragraph (bez zaleznosci od config katalogu):
 *   - back-pill (link do parent - button_name + button_url)
 *   - zielona pigulka kategorii (text_1)
 *   - H1 tytul (name)
 *   - lead (text - HTML)
 */
export function ParagraphDarkHeroBack({
  paragraph,
  t: _t,
}: {
  paragraph: ParagraphDarkHeroBackData;
  t: ParagraphTranslations;
}) {
  const {
    text_1: category,
    name: title,
    text: lead,
    benefits,
    button_name: backLabel,
    button_url: backUrl,
  } = paragraph;

  const hasCategory = category && category.trim() !== "";
  const hasLead = lead && lead.trim() !== "";
  const hasBenefits = Array.isArray(benefits) && benefits.length > 0;
  const hasBack = backLabel && backLabel.trim() !== "" && backUrl && backUrl.trim() !== "";
  const backHref = hasBack ? extractInternalHref(backUrl) : null;

  return (
    <section className={styles.section}>
      <div className={styles.inner}>
        {hasBack || hasCategory ? (
          <div className={styles.badges}>
            {hasBack ? (
              backHref !== null ? (
                <Link href={backHref} className={styles.backPill}>
                  <span className={styles.backPillIcon} aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                  {backLabel}
                </Link>
              ) : (
                <a href={backUrl} className={styles.backPill}>
                  <span className={styles.backPillIcon} aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                  {backLabel}
                </a>
              )
            ) : null}
            {hasCategory ? (
              <span className={styles.categoryPill}>{category}</span>
            ) : null}
          </div>
        ) : null}

        {title ? <h1 className={styles.title}>{title}</h1> : null}
        {hasLead ? <HtmlText html={lead} className={styles.lead} /> : null}

        {hasBenefits ? (
          <ul className={styles.benefits}>
            {benefits.map((benefit, idx) => (
              <li key={idx} className={styles.benefit}>
                <span className={styles.benefitCheck} aria-hidden="true" />
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </section>
  );
}
